import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { DashboardConfiguration } from 'types/dashboard';
import { DashboardConfigurationSchema } from 'types/dashboard_schema';

import { DASHBOARD_TEMPLATES } from 'dashboards/services/dashboard_templates';
import { readFile } from 'services/files';
import { useAPI } from 'hooks/api/useAPI';
import { useCreateDashboard } from 'dashboards/hooks/useCreateDashboard';
import { useModal } from 'hooks/useModal';
import { useTimeRangeParams } from 'dashboards/hooks/useTimeRangeParams';

import * as Dialog from 'components/Dialog';
import { Button } from 'components/Button/Button';
import { Dropzone } from 'components/Dropzone/Dropzone';
import { Field } from 'components/Field/Field';
import { FileDisplay } from 'components/FileDisplay/FileDisplay';
import { FormErrorMessage } from 'components/FormMessage/FormMessage';
import { Heading } from 'components/Heading/Heading';
import { Input } from 'components/Input/Input';
import { Select } from 'components/Select/Select';
import { SeparatorWithLabel } from 'components/SeparatorWithLabel/SeparatorWithLabel';

const TEMPLATE_OPTIONS = Object.entries(DASHBOARD_TEMPLATES).map(
  ([template, { templateName }]) => ({ label: templateName, value: template })
);

type CreateDashboardFieldValues = {
  name: string;
  template: string | null;
  file: File | null;
};

type CreateDashboardDialogProps = Pick<Dialog.RootProps, 'open'>;

export function CreateDashboardDialog({ open }: CreateDashboardDialogProps) {
  const { close } = useModal();

  return (
    <Dialog.Root
      open={open}
      className="min-w-md"
      onOpenChange={(open) => !open && close()}
    >
      <Dialog.Title asChild>
        <Heading level="2">Create new dashboard</Heading>
      </Dialog.Title>
      <CreateDashboardDialogContent />
    </Dialog.Root>
  );
}

function CreateDashboardDialogContent() {
  const navigate = useNavigate();
  const { applicationID } = useAPI();
  const { close } = useModal();

  const { preset, start, stop, timezone } = useTimeRangeParams();
  const search = new URLSearchParams({
    preset,
    start,
    stop,
    timezone,
  }).toString();

  const {
    clearErrors,
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    setError,
    setValue,
    watch,
  } = useForm<CreateDashboardFieldValues>();

  const {
    isLoading: isCreatingDashboard,
    error,
    mutate: createDashboard,
  } = useCreateDashboard({
    onSuccess(dashboard) {
      navigate({
        pathname: `/applications/${applicationID}/monitor/dashboards/${dashboard.id}`,
        search,
      });
      close();
    },
  });

  async function createDashboardFromJsonFile({
    name,
    file,
  }: Omit<CreateDashboardFieldValues, 'template'>) {
    if (!file) {
      return;
    }

    let content;

    try {
      content = await readFile(file);
    } catch {
      setError('file', { message: 'Failed to read file.' });
      return;
    }

    let json;

    try {
      json = JSON.parse(content);
    } catch {
      setError('file', { message: 'Failed to parse json.' });
      return;
    }

    let dashboardConfiguration: DashboardConfiguration;

    try {
      dashboardConfiguration = DashboardConfigurationSchema.parse(json);
    } catch {
      setError('file', {
        message: 'The dashboard configuration is not valid.',
      });
      return;
    }

    createDashboard({
      name,
      configuration: dashboardConfiguration,
    });
  }

  async function onSubmit({
    name,
    template,
    file,
  }: CreateDashboardFieldValues) {
    if (file) {
      await createDashboardFromJsonFile({ name, file });
      return;
    }

    const configuration = template
      ? DASHBOARD_TEMPLATES[template].configuration
      : { widgets: [] };

    createDashboard({
      name,
      configuration,
    });
  }

  function handleDeleteFile() {
    setValue('file', null);
    clearErrors('file');
  }

  const currentFile = watch('file');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Field label="Dashboard name" error={errors.name} required>
        <Input
          {...register('name', {
            required: 'Please choose a name for this dashboard.',
          })}
          type="text"
          autoComplete="off"
          spellCheck="false"
          autoFocus
        />
      </Field>

      <Field label="Import from JSON" error={errors.file}>
        <Controller
          name="file"
          control={control}
          render={({ field: { onChange } }) => (
            <Dropzone
              onChange={([file]) => {
                if (file) {
                  onChange(file);

                  // Clear template selection
                  setValue('template', null);
                }
              }}
              accept="application/json"
            />
          )}
        />

        {currentFile && (
          <FileDisplay
            file={currentFile}
            onDelete={handleDeleteFile}
            isError={Boolean(errors.file)}
          />
        )}
      </Field>

      <SeparatorWithLabel label="Or use a template" />

      <Field
        label="Template"
        info="Skip selection to create empty dashboard"
        error={errors.template}
      >
        <Controller
          name="template"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Select
              value={
                value
                  ? { label: DASHBOARD_TEMPLATES[value].templateName, value }
                  : null
              }
              onChange={(option) => {
                onChange(option ? option.value : option);

                // Set dashboard name to template name if empty
                if (option && !getValues('name')) {
                  setValue('name', option.label);
                }

                // Clear imported dashboard
                if (option && option.value) {
                  setValue('file', null);
                }
              }}
              options={TEMPLATE_OPTIONS}
              isClearable
            />
          )}
        />
      </Field>

      <FormErrorMessage error={error} />

      <div className="flex justify-end gap-2">
        <Dialog.Close asChild>
          <Button variant="secondary">Cancel</Button>
        </Dialog.Close>
        <Button variant="primary" type="submit" loading={isCreatingDashboard}>
          Create
        </Button>
      </div>
    </form>
  );
}
